import { z } from 'zod';
import { SchemaEmail } from '@axiom/types';

export const InterviewAttendeesAndLinkModalSchema = z
  .object({
    // Form-level handle for the "at least one attendee" error. The FormGroup
    // name="attendees" wrapping the Dropdown and ad-hoc rows subscribes to
    // this key so the error has a render site even when the Dropdown is
    // gated out by an account with no contacts. The field carries no value
    // and stays optional; only the superRefine below ever populates it.
    attendees: z.string().optional(),
    contacts: z.array(z.string().uuid()),
    adHocContacts: z.array(
      z.object({
        firstName: z.string().min(1).trim(),
        lastName: z.string().min(1).trim(),
        email: SchemaEmail,
        readonly: z.boolean(),
      })
    ),
    meetingLinkType: z.enum(['ZOOM', 'MANUAL']),
    // Nullish, not a plain required string: the shared Input writes `null` when
    // its field is cleared, and a bare z.string() would then surface a base
    // "Required" type error even under ZOOM (where the link is irrelevant). The
    // superRefine below enforces presence only when MANUAL is selected.
    meetingLink: z.string().nullish(),
  })
  .superRefine((values, ctx) => {
    if (
      values.contacts.length === 0 &&
      values.adHocContacts.filter(c => c.email).length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attendees'],
        message: 'At least one contact must be selected or added.',
      });
    }

    if (values.meetingLinkType === 'MANUAL') {
      if (!values.meetingLink) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['meetingLink'],
          message: 'A meeting link is required.',
        });
      } else if (!z.string().url().safeParse(values.meetingLink).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['meetingLink'],
          message: 'Please enter a valid URL.',
        });
      } else if (!values.meetingLink.startsWith('https://')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['meetingLink'],
          message: 'Meeting link must start with https://',
        });
      }
    }
  });

export type InterviewAttendeesAndLinkModal = z.infer<
  typeof InterviewAttendeesAndLinkModalSchema
>;
